/**
 * useFeatureFlags — fetches clinic feature flags and computes trial/gating state.
 *
 * Provides:
 *  - flags[]           — raw feature flag list from API
 *  - getFlag(feature)  — lookup a single flag
 *  - nearestExpiry     — the trial flag expiring soonest (drives S1 banner)
 *  - expiredFlags      — flags that expired in the last 72h (drives S2 modal)
 *  - severity          — "healthy" | "warning" | "critical" | "expired" | null
 *  - isAdmin           — true if current user is Admin
 *  - loading           — true while fetching
 */

import { useState, useEffect, useCallback } from "react";
import { apiRequest } from "../services/api.client";
import { OnboardingAPI } from "../services/onboarding.service";
import { BillingAPI } from "../services/billing.service";

export interface FeatureFlag {
  feature: string;
  is_enabled: boolean;
  expires_at: string | null;
  is_trial: boolean;
}

export type Severity = "healthy" | "warning" | "critical" | "expired" | null;

export interface FeatureFlagState extends FeatureFlag {
  days_left: number | null;
  expired: boolean;
  display_label: string;
  icon: string;
}

const FEATURE_META: Record<string, { label: string; icon: string }> = {
  ai_scribe:              { label: "AI Scribe",                   icon: "🎙️" },
  cloud_storage:          { label: "Cloud Storage",               icon: "☁️" },
  whatsapp_notifications: { label: "WhatsApp Notifications",      icon: "💬" },
  advanced_reports:       { label: "Advanced Reports & Analytics", icon: "📊" },
  abdm_sync:              { label: "ABDM Health Records Sync",    icon: "🔗" },
  // Gated on clinical schedule sign-off, not payment - defaults OFF for every
  // clinic (see requires_feature's bypass_hybrid=False call site on the
  // backend). Flip is Sid's call once the NIS/IAP table is confirmed.
  immunization:           { label: "Pediatric Vaccination Schedule", icon: "💉" },
  pediatric_growth_charts: { label: "Pediatric Growth Charts",    icon: "📈" },
};

function daysLeft(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

function expiredWithin72h(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  const exp = new Date(expiresAt).getTime();
  const now = Date.now();
  return exp < now && (now - exp) < 72 * 60 * 60 * 1000;
}

function computeSeverity(days: number | null, expired: boolean): Severity {
  if (expired) return "expired";
  if (days === null) return null;
  if (days <= 2) return "critical";
  if (days <= 7) return "warning";
  return "healthy";
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlagState[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  const fetchFlags = useCallback(async () => {
    try {
      const [rawFlags, me, subStatus] = await Promise.all([
        apiRequest<FeatureFlag[]>("/clinic/feature-flags"),
        OnboardingAPI.getMe(),
        BillingAPI.getSubscription().catch(() => null),
      ]);

      // Check if subscription itself is expired (covers paid + trial)
      setSubscriptionExpired(
        subStatus?.has_subscription === true &&
        (subStatus?.status === "expired" || subStatus?.status === "halted")
      );

      setIsAdmin(me.role === "Admin");

      const enriched: FeatureFlagState[] = (rawFlags || []).map(f => {
        const days = daysLeft(f.expires_at);
        const exp = isExpired(f.expires_at);
        const meta = FEATURE_META[f.feature] || { label: f.feature, icon: "⚡" };
        return {
          ...f,
          days_left: days,
          expired: exp,
          display_label: meta.label,
          icon: meta.icon,
        };
      });

      setFlags(enriched);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  // Re-fetch flags when subscription changes (AppSync real-time push)
  useEffect(() => {
    const handler = () => { fetchFlags(); };
    window.addEventListener("billing:subscription-changed", handler);
    return () => window.removeEventListener("billing:subscription-changed", handler);
  }, [fetchFlags]);

  // Derived state
  const trialFlags = flags.filter(f => f.is_trial && f.expires_at);

  // Nearest expiry — the trial flag with the fewest days_left
  const nearestExpiry = trialFlags
    .filter(f => !f.expired)
    .sort((a, b) => (a.days_left ?? Infinity) - (b.days_left ?? Infinity))[0] || null;

  // Expired flags: trial expired within 72h OR subscription-level expiry (any disabled flag)
  const expiredFlags = subscriptionExpired
    ? flags.filter(f => !f.is_enabled || f.expired)
    : flags.filter(f => f.is_trial && expiredWithin72h(f.expires_at));

  // Count of trials ending this week
  const trialsEndingThisWeek = trialFlags.filter(f => !f.expired && f.days_left !== null && f.days_left <= 7).length;

  // Overall severity (based on nearest expiry or subscription expiry)
  const severity: Severity = subscriptionExpired
    ? "expired"
    : nearestExpiry
      ? computeSeverity(nearestExpiry.days_left, nearestExpiry.expired)
      : expiredFlags.length > 0 ? "expired" : null;

  const getFlag = (feature: string) => flags.find(f => f.feature === feature) || null;

  // Check if a specific feature is accessible (enabled and not expired)
  const isFeatureAccessible = (feature: string): boolean => {
    const flag = getFlag(feature);
    if (!flag) return false;
    return flag.is_enabled && !flag.expired;
  };

  // Get gate state for a feature: "active" | "trial" | "locked" | "expired"
  const getGateState = (feature: string): "active" | "trial" | "locked" | "expired" => {
    const flag = getFlag(feature);
    if (!flag) return "locked";
    if (flag.expired) return "expired";
    if (flag.is_enabled && flag.is_trial) return "trial";
    if (flag.is_enabled) return "active";
    return "locked";
  };

  return {
    flags,
    loading,
    isAdmin,
    error,
    nearestExpiry,
    expiredFlags,
    trialsEndingThisWeek,
    severity,
    subscriptionExpired,
    getFlag,
    isFeatureAccessible,
    getGateState,
    refetch: fetchFlags,
  };
}
